import { DOCUMENT } from '@angular/common';
import { Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { CaptchaService } from 'src/app/home/services/captcha.service';
import { Subject, debounceTime, take, takeUntil } from 'rxjs';
import { AuthTypes, ValidatePhoneRequest } from '../../interfaces/auth.interface';
import { AuthAPIService } from '../../services/auth-api.service';
import { FormControl } from '@angular/forms';
import * as Sentry from '@sentry/angular';
import { extractPhoneNumber, normalizePhoneCountryCode } from 'src/app/utils/phone-utils';

@Component({
  selector: 'app-country-phone-prefix-selector',
  templateUrl: './country-phone-prefix-selector.component.html',
  styleUrls: ['./country-phone-prefix-selector.component.scss'],
  standalone: false,
})
export class CountryPhonePrefixSelectorComponent implements OnInit, OnDestroy {
  @Input() control: FormControl;
  @Input() authType = AuthTypes.Login;
  @Output() countryPrefixChange: EventEmitter<string> = new EventEmitter<string>();

  preferredCountries = ['nl', 'be'];
  countryPrefix: string;
  destroy$ = new Subject<boolean>();
  readonly outputNumberFormatE164 = 0;

  customErrors = {
    api_phone_validation_error: false,
    user_exists: false,
    required: false,
  };

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private captchaService: CaptchaService,
    private authApiService: AuthAPIService
  ) {}

  ngOnInit() {
    if (this.authType === AuthTypes.Register) {
      this.control.valueChanges.pipe(takeUntil(this.destroy$), debounceTime(400)).subscribe((value) => {
        if (value) {
          this.validatePhone();
        }
      });
    }
  }

  get phoneHTMLInput() {
    return this.document.querySelector('.country-tel-prefix-container input') as HTMLInputElement;
  }

  onCountryChange(countryCode: string) {
    if (this.phoneHTMLInput && !this.phoneHTMLInput.value) {
      setTimeout(() => {
        this.control.markAsUntouched();
      }, 50);
    }

    const normalizedPrefix = normalizePhoneCountryCode(countryCode || '');
    this.countryPrefix = normalizedPrefix;
    this.countryPrefixChange.emit(normalizedPrefix);
  }

  get controlErrorKeys() {
    if (this.control?.errors) {
      return Object.keys(this.control.errors);
    }
  }

  validatePhone() {
    if (this.control?.value) {
      this.captchaService
        .execute()
        .pipe(take(1))
        .subscribe((token) => {
          const phoneCountryCode = normalizePhoneCountryCode(this.countryPrefix);
          const body: ValidatePhoneRequest = {
            phone_number: extractPhoneNumber(this.control?.value, phoneCountryCode),
            phone_country_code: phoneCountryCode,
            h_captcha_response: token,
          };

          if (body?.phone_number?.length) {
            this.authApiService.validatePhone(body).subscribe(
              (res) => {
                if (!res?.is_valid) {
                  this.control.setErrors({ api_phone_validation_error: true });
                }

                if (res?.code === 'user_exists') {
                  this.control.setErrors({ user_exists: true });
                }
              },
              (error) => {
                Sentry.captureException(error.error || error, {
                  tags: {
                    module: 'auth-dialog',
                  },
                });
              }
            );
          }
        });
    }
  }

  ngOnDestroy() {
    this.destroy$.next(true);
  }
}
